import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ColDef, RowClickedEvent } from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";
import { useCustomer, useSaleOrder, useSaleOrderStore, useWmsItem } from "hooks";
import { SaleOrder, SaleOrderItem, SaleOrderQuery } from "types";
import { ROUTES } from "utils";
import SnackbarUtils from "utils/SnackbarUtils";

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("vi-VN");
};

const compareDate = (filterDate: Date, cellValue?: string) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return 1;
  const cellOnly = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate(),
  );
  if (cellOnly < filterDate) return -1;
  if (cellOnly > filterDate) return 1;
  return 0;
};

const parseNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getUnitString = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "object") {
    return value.name ?? value.code ?? "";
  }
  return "";
};

const getItemAmount = (item: SaleOrderItem): number => {
  const quantity = parseNumber(item.quantity);
  const price = parseNumber(item.price);
  const baseAmount = quantity * price;
  const discountRate =
    item.discount_rate !== undefined && item.discount_rate !== null
      ? Number(item.discount_rate)
      : 0;
  const discountAmount =
    discountRate > 0 ? (baseAmount * discountRate) / 100 : 0;
  const otherReduction =
    item.other_reduction !== undefined && item.other_reduction !== null
      ? parseNumber(item.other_reduction)
      : 0;
  if ((item as any).amount !== undefined && item.amount !== null) {
    return parseNumber((item as any).amount);
  }
  return Math.max(baseAmount - discountAmount - otherReduction, 0);
};

const getItemVatAmount = (item: SaleOrderItem): number => {
  const vatRate =
    item.vat_rate !== undefined && item.vat_rate !== null
      ? Number(item.vat_rate)
      : 0;
  if ((item as any).vat_amount !== undefined && item.vat_amount !== null) {
    return parseNumber((item as any).vat_amount);
  }
  return vatRate > 0 ? (getItemAmount(item) * vatRate) / 100 : 0;
};

const sanitizeItems = (
  items?: SaleOrderItem[],
  wmsMap?: Map<number | string, any>,
) =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const productId =
            item.product_id !== undefined && item.product_id !== null
              ? item.product_id
              : "";
          const selected =
            wmsMap?.get(productId) ||
            wmsMap?.get(item.wms_id as any) ||
            wmsMap?.get(String(productId)) ||
            wmsMap?.get(String(item.wms_id ?? ""));
          const amount = getItemAmount(item);
          const vatAmount = getItemVatAmount(item);

          return {
            ...(item.id ? { id: item.id } : {}),
            product_id: productId,
            wms_id:
              item.wms_id !== undefined && item.wms_id !== null
                ? item.wms_id
                : undefined,
            unit_id:
              item.unit_id !== undefined && item.unit_id !== null
                ? item.unit_id
                : (item as any)?.unit?.id,
            product_name:
              (item.product_name
                ? String(item.product_name).trim()
                : undefined) ??
              (selected?.name ? String(selected.name) : undefined),
            unit:
              item.unit && String(item.unit).trim()
                ? String(item.unit).trim()
                : getUnitString(selected?.unit) || undefined,
            quantity: parseNumber(item.quantity),
            price: parseNumber(item.price),
            amount,
            discount_rate:
              item.discount_rate !== undefined ? Number(item.discount_rate) : undefined,
            other_reduction:
              item.other_reduction !== undefined
                ? parseNumber(item.other_reduction)
                : undefined,
            vat_rate: item.vat_rate !== undefined ? Number(item.vat_rate) : undefined,
            vat_amount: vatAmount,
            converted_unit: item.converted_unit,
            conversion_type: item.conversion_type,
            conversion_rate:
              item.conversion_rate !== undefined
                ? parseNumber(item.conversion_rate)
                : undefined,
            converted_quantity:
              item.converted_quantity !== undefined
                ? parseNumber(item.converted_quantity)
                : undefined,
            converted_price:
              item.converted_price !== undefined
                ? parseNumber(item.converted_price)
                : undefined,
          };
        })
        .filter(
          (item) =>
            Number(item.quantity ?? 0) > 0 ||
            Number(item.price ?? 0) > 0 ||
            Number(item.amount ?? 0) > 0 ||
            Boolean(item.product_id) ||
            Boolean(item.product_name),
        )
    : [];

const withItemFields = (
  order: SaleOrder,
  wmsMap?: Map<number | string, any>,
): SaleOrder => {
  const normalizedItems = sanitizeItems(order.items, wmsMap);
  const total = normalizedItems.reduce(
    (sum, item) => sum + getItemAmount(item) + getItemVatAmount(item),
    0,
  );
  return {
    ...order,
    items: normalizedItems,
    ...(order.discount_rate !== undefined ? {} : { discount_rate: 0 }),
    ...(order.vat_rate !== undefined ? {} : { vat_rate: 0 }),
    ...(order as any).total_amount === undefined
      ? { total_amount: total }
      : {},
  } as any;
};

const SaleOrders: React.FC = () => {
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState<SaleOrderQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<SaleOrder | null>(null);
  const [selectedItems, setSelectedItems] = useState<SaleOrderItem[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");

  const { rows, setRows, removeRow, isLoading, setLoading } =
    useSaleOrderStore();

  const { fetchList, deleteSaleOrder } = useSaleOrder();
  const { fetchList: fetchWmsItems } = useWmsItem();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );
  const { data: wmsItems = [] } = fetchWmsItems(true);
  const wmsItemMap = useMemo(() => {
    const map = new Map<number | string, any>();
    (wmsItems ?? []).forEach((item: any) => {
      map.set(item.id, item);
      map.set(String(item.id), item);
      if (item.wms_id !== undefined && item.wms_id !== null) {
        map.set(item.wms_id, item);
        map.set(String(item.wms_id), item);
      }
    });
    return map;
  }, [wmsItems]);

  const { data: saleOrderData, isFetching } = fetchList(queryParams, true);

  useEffect(() => {
    if (saleOrderData?.data) {
      const mapped =
        (saleOrderData.data as SaleOrder[])?.map((item) =>
          withItemFields(item, wmsItemMap),
        ) ?? [];
      setRows(mapped);
      if (mapped.length) {
        setSelectedItems(mapped[0].items ?? []);
        setSelectedLabel(
          (mapped as any)[0]?.code ??
            mapped[0].buyer_name ??
            `Đơn ${mapped[0].id}`,
        );
      } else {
        setSelectedItems([]);
        setSelectedLabel("");
      }
    }
  }, [saleOrderData, setRows, wmsItemMap]);

  useEffect(() => {
    setLoading(Boolean(isFetching || deleteSaleOrder.isPending));
  }, [deleteSaleOrder.isPending, isFetching, setLoading]);

const customerLabelById = useMemo(() => {
  const map = new Map<number | string, string>();
  (customersData?.data as any[])?.forEach((c) => {
    const label = `${c.code}${c.name ? ` - ${c.name}` : ""}`;
    map.set(c.id, label);
  });
  return map;
}, [customersData]);

const paymentMethodLabel: Record<string, string> = {
  cash: "Tiền mặt",
  bank: "Ngân hàng",
  debt: "Công nợ",
};

const invoiceTypeLabel: Record<string, string> = {
  no_invoice: "Không xuất hóa đơn",
  vat: "Hóa đơn VAT",
};

const columns = useMemo<ColDef<SaleOrder>[]>(() => {
  return [
    {
      headerName: "Ngày bán",
      field: "order_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Đến hạn",
        field: "due_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Mã đơn",
        field: "order_code",
        minWidth: 140,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Khách hàng",
        field: "customer_id",
        minWidth: 200,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return "";
          const label = customerLabelById.get(params.value);
          return label ? `${label}` : String(params.value);
        },
      },
      {
        headerName: "Người mua",
        field: "buyer_name",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Địa chỉ",
        field: "buyer_address",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Số hóa đơn",
        field: "invoice_number",
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Ngày hóa đơn",
        field: "invoice_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        headerName: "Phương thức",
        field: "payment_method",
        minWidth: 160,
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          const value = params.value;
          if (value === undefined || value === null) return "";
          return paymentMethodLabel[String(value)] ?? String(value);
        },
      },
      {
        headerName: "Loại hóa đơn",
        field: "invoice_type",
        minWidth: 160,
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          const value = params.value;
          if (value === undefined || value === null) return "";
          return invoiceTypeLabel[String(value)] ?? String(value);
        },
      },
      {
        headerName: "CK (%)",
        field: "discount_rate",
        minWidth: 130,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? `${Number(params.value)}%`
            : "",
      },
      {
        headerName: "VAT (%)",
        field: "vat_rate",
        minWidth: 130,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? `${Number(params.value)}%`
            : "",
      },
    ];
  }, [customerLabelById]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      flex: 1,
      minWidth: 140,
      editable: false,
    }),
    [],
  );

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const handleAdd = useCallback(() => {
    navigate(ROUTES.ACCOUNTING.SALES_DOCUMENT_CREATE);
  }, [navigate]);

  const handleEditRow = useCallback(
    (params: any) => {
      if (params?.data?.id) {
        navigate(`${ROUTES.ACCOUNTING.SALES_DOCUMENT_DETAIL}/${params.data.id}`);
      }
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (event: RowClickedEvent<SaleOrder>) => {
      const items = sanitizeItems(event.data?.items, wmsItemMap);
      setSelectedItems(items);
      setSelectedLabel(
        event.data?.order_code ??
          event.data?.buyer_name ??
          `Đơn ${event.data?.id ?? ""}`,
      );
    },
    [wmsItemMap],
  );

  const handleDeleteRow = useCallback((row: SaleOrder) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteSaleOrder.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa đơn bán hàng không thành công");
      },
    });
  }, [confirmRow, deleteSaleOrder, removeRow]);

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">Đơn bán hàng</h1>
        <p className="text-sm text-gray-600">
          Danh sách đơn bán hàng. Chọn một dòng để xem chi tiết hoặc thêm mới.
        </p>
      </div>

      <div className="relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-end">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleAdd}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Plus className="h-4 w-4 text-green-700" />
              Thêm đơn bán hàng
            </button>
          </div>
        </div>

        <div className="mb-4 space-y-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Chi tiết hàng hóa {selectedLabel ? `- ${selectedLabel}` : ""}
            </h3>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">STT</th>
                  <th className="px-3 py-2 text-left font-semibold">Mã hàng</th>
                  <th className="px-3 py-2 text-left font-semibold">Tên hàng</th>
                  <th className="px-3 py-2 text-right font-semibold">SL</th>
                  <th className="px-3 py-2 text-right font-semibold">Đơn giá</th>
                  <th className="px-3 py-2 text-right font-semibold">Thành tiền</th>
                  <th className="px-3 py-2 text-right font-semibold">VAT</th>
                  <th className="px-3 py-2 text-left font-semibold">Đơn vị</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {selectedItems.length ? (
                  selectedItems.map((item, index) => (
                    <tr key={`item-${index}`} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2 text-gray-900">
                        {(item as any).product_id ?? ""}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.product_name ?? ""}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {parseNumber(item.quantity).toLocaleString("vi-VN")}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {parseNumber(item.price).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {getItemAmount(item).toLocaleString("vi-VN")} ₫
                      </td>
                      <td className="px-3 py-2 text-right text-gray-900">
                        {(() => {
                          const vatAmount = getItemVatAmount(item);
                          if (
                            vatAmount > 0 ||
                            (item?.vat_rate !== undefined &&
                              item.vat_rate !== null)
                          ) {
                            return `${vatAmount.toLocaleString("vi-VN")} ₫`;
                          }
                          return "";
                        })()}
                      </td>
                      <td className="px-3 py-2 text-gray-900">
                        {item.unit ?? item.converted_unit ?? item.unit_id ?? ""}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-6 text-center text-gray-500"
                    >
                      Chọn đơn bán hàng để xem chi tiết hàng hóa.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AgGridTable
          rows={rows}
          columns={columns}
          height={620}
          autoHeight={false}
          defaultColDef={defaultColDef}
          totalPages={saleOrderData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
          getRowId={(params) => String(params.data.id)}
        />

        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách đơn bán hàng, vui lòng chờ giây lát."
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa đơn bán hàng?"
        description={`Bạn chắc chắn muốn xóa đơn "${confirmRow?.order_code ?? ""}"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteSaleOrder.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default SaleOrders;
